import { Address } from "./company";
import { Product } from "./product";

export type CartItem = {
  product: Product;
  quantityRequired?: {
    value: number;
    unit: string; // e.g., "in", "cm"
  };
  quantityRequiredString?: string; // e.g., "10 in"
  /**
   * What this line is worth on a saved quote/PO, computed server-side with the
   * price unit converted to the quantity unit (a per-lb price times the
   * piece's weight). Absent on carts that were never priced by the server.
   */
  lineAmount?: number | null;
  /**
   * On quotes made from an RFQ: the RFQ line this item answers, what the
   * customer asked for in their words, and how the item differs from it (empty
   * when it is exactly what was requested). The customer email, the quote
   * page and internal notices all stress those differences.
   */
  rfqLine?: {
    id: string;
    requested: string;
    differences: string[];
    notes: string[];
  };
  /**
   * On quotes made from an RFQ: further items the operator offers for this
   * line, which the customer may switch to (the quote's next version then
   * carries the chosen one). Not counted in the totals.
   */
  alternatives?: { product: Product; lineAmount?: number | null }[];
  deliveryAddress: Address;
  millCertRequired: boolean;
  notes: string; // Additional notes for the item
  shippingCost?: {
    available: boolean;
    cost?: number; // Cost in USD
    estimatedDeliveryDate?: string; // ISO date string
    quoteNumber?: string; // Reference to the shipping quote
    quoteExpirationDateTime?: string; // ISO date string
    carrierKey?: string; // Carrier identifier
  };
  selectedShippingOption?: ShippingSelectionPreference;
  shippingOptions?: ShipmentOption[]; // List of available shipping options for the item
};

export type ShippingSelectionPreference = "cheapest" | "fastest";

export type CartCheckoutDefaults = {
  name?: string;
  email?: string;
  company?: string;
  phone?: string;
  /** Who receives the goods at the ship-to (from a quote's freight plan), editable at checkout. */
  deliveryContact?: { company?: string; name?: string; phone?: string; email?: string };
  /** Delivery hours, call ahead, dock, liftgate... (from a quote's freight plan), editable at checkout. */
  deliveryInstructions?: string;
};

export type Cart = {
  cartItems: CartItem[];
  totalShippingOverride?: ShipmentOption;
  checkoutDefaults?: CartCheckoutDefaults;
};

export type ShipmentOption = {
  // If this is a quote that we can refer to later for booking, this is the
  // reference we can use to identify it. Otherwise, this can be null or
  // undefined.
  vendorShipmentReference?: string;
  serviceCode?: string;
  totalCharges: {
    currencyCode: string;
    monetaryValue: number;
  };
  estimatedDeliveryDate?: {
    date: string; // ISO 8601 date string
    time?: string; // ISO 8601 time string, optional
  };
  vendorId: string;
  isGuaranteed: boolean;
  vendorResponse: any; // Store the raw response from the carrier for debugging and future reference
  // Wording an operator chose for this option in Internal > Tools > Shipping
  // Configuration. Absent means the cart uses its own wording.
  displayLabel?: string;
  // Why this option was quoted (or why we fell back to the manual placeholder),
  // written when the quote is made. Internal > Orders shows it on the line.
  decisionNote?: string;
};

const emptyCart = (): Cart => ({
  cartItems: [],
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const normalizeCart = (value: unknown): Cart => {
  if (Array.isArray(value)) {
    return {
      cartItems: value as CartItem[],
    };
  }

  if (!isRecord(value)) {
    return emptyCart();
  }

  return {
    cartItems: Array.isArray(value.cartItems)
      ? (value.cartItems as CartItem[])
      : [],
    totalShippingOverride: isRecord(value.totalShippingOverride)
      ? (value.totalShippingOverride as ShipmentOption)
      : undefined,
    checkoutDefaults: isRecord(value.checkoutDefaults)
      ? {
          name:
            typeof value.checkoutDefaults.name === "string"
              ? value.checkoutDefaults.name
              : undefined,
          email:
            typeof value.checkoutDefaults.email === "string"
              ? value.checkoutDefaults.email
              : undefined,
          company:
            typeof value.checkoutDefaults.company === "string"
              ? value.checkoutDefaults.company
              : undefined,
          phone:
            typeof value.checkoutDefaults.phone === "string"
              ? value.checkoutDefaults.phone
              : undefined,
          deliveryContact: isRecord(value.checkoutDefaults.deliveryContact)
            ? {
                company: typeof value.checkoutDefaults.deliveryContact.company === "string" ? value.checkoutDefaults.deliveryContact.company : undefined,
                name: typeof value.checkoutDefaults.deliveryContact.name === "string" ? value.checkoutDefaults.deliveryContact.name : undefined,
                phone: typeof value.checkoutDefaults.deliveryContact.phone === "string" ? value.checkoutDefaults.deliveryContact.phone : undefined,
                email: typeof value.checkoutDefaults.deliveryContact.email === "string" ? value.checkoutDefaults.deliveryContact.email : undefined,
              }
            : undefined,
          deliveryInstructions:
            typeof value.checkoutDefaults.deliveryInstructions === "string"
              ? value.checkoutDefaults.deliveryInstructions
              : undefined,
        }
      : undefined,
  };
};

export const parseStoredCart = (storedCart: string | null): Cart => {
  if (!storedCart) {
    return emptyCart();
  }

  try {
    return normalizeCart(JSON.parse(storedCart));
  } catch (error) {
    console.error("Failed to parse stored cart:", error);
    return emptyCart();
  }
};
